import { AxiosMethods } from '@setvi/shared/enums';
import { Product } from '@setvi/shared/interfaces';
import { axiosHelper } from '@setvi/shared/services';
import { useMutation } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import type { AxiosError } from 'axios';

enum Endpoints {
  Price = '/pricing/get',
  PriceGroup = '/pricing-groups/pricing'
}

export enum PriceType {
  Price = 'Price',
  PriceGroup = 'PriceGroup'
}

// The backend maps these to its pricing modules (quote -> QUOTES,
// order -> ORDERS, omitted -> CATALOG) and still validates `type` against these
// two values only. Do not switch to QUOTES / ORDERS / CATALOG until
// GetProductPricesRequest.type accepts them, or the request is rejected (DEV-540).
export enum PricingType {
  Quote = 'quote',
  Order = 'order'
}

/**
 * Stand-in `accountNumber` for pricing calls on a quote that has neither a
 * customer nor a segment — a prospect/lead quote. The pricing service needs
 * *an* account to answer at all, and sending nothing left those items silently
 * unpriced. This value can't collide with a real account number or a Mongo
 * segment id, so it reads unambiguously as "no account" in the API logs.
 * QOT-1531.
 */
export const PRICING_FALLBACK_ACCOUNT = '0000';

/** Treats null / undefined / '' alike — call sites pass all three. */
export const resolvePricingAccountNumber = (accountNumber?: string | null) =>
  accountNumber?.trim() || PRICING_FALLBACK_ACCOUNT;

interface MutationProps {
  type?: PriceType;
  body: {
    type?: PricingType;
    /** May be empty; normalized via `resolvePricingAccountNumber` on the way out. */
    accountNumber?: string;
    deliveryDate?: string;
    products: {
      sku: string;
      quantity: number;
    }[];
  };
}

type MinimalProduct = Pick<Product, 'sku' | 'quantity'> & { id?: string };

/**
 * A priced row. `POST /pricing/get` can answer with a row whose `sell` is
 * **null** instead of omitting the SKU, so "row present but `sell == null`" is a
 * per-item failure signal — distinct from "SKU missing from the response
 * entirely", which `getPrices` reports as a synthetic failed row (DEV-495).
 */
export interface ProductPrice extends Partial<Product> {
  sku: string | null;
  id: string | null;
  /**
   * `null` when pricing produced nothing usable — NOT 0. Callers must not treat
   * a failure as a free item; `pricingFailed` says which is which.
   */
  price: number | null;
  pricingFailed: boolean;
  pricingFailureReason: string | null;
}

export interface GetPricesProps {
  type?: PriceType;
  pricingType?: PricingType;
  productList: MinimalProduct[];
  /**
   * The customer's account number, or the segment id when there's no customer.
   * Optional: a prospect quote has neither, and the placeholder is applied for
   * you on the way out (QOT-1531).
   */
  accountNumber?: string;
  deliveryDate?: string;
}

export const getProductsPrices = () => ({
  mutationFn: ({ type, body }: MutationProps) =>
    axiosHelper({
      endpoint: Endpoints[(type || PriceType.Price) as keyof typeof Endpoints],
      method: AxiosMethods.POST,
      // Normalized here rather than at each call site so no caller can send an
      // empty account and get silently unpriced items back (QOT-1531).
      body: {
        ...body,
        accountNumber: resolvePricingAccountNumber(body?.accountNumber)
      },
      baseURL: process.env.REVOPS_API
    })
});

/** Shown when the response gives no reason of its own. */
const NO_PRICE_RETURNED = 'no price was returned';
const SKU_NOT_PRICED = 'no pricing found for this SKU';

export const useProductsPrices = () => {
  const { mutationFn } = getProductsPrices();
  const mutation = useMutation(mutationFn, {
    // The `catch` below reports the failure itself, in the same words on every
    // screen that prices through here. Without this the global MutationCache
    // handler enqueues a SECOND toast for the same failure. Slack reporting is
    // untouched — it runs before this check.
    meta: { ownErrorMessage: true }
  });

  const getPrices = async ({
    type = PriceType.Price,
    pricingType,
    productList,
    accountNumber,
    deliveryDate
  }: GetPricesProps): Promise<ProductPrice[] | null> => {
    try {
      const products = productList?.map(product => ({
        sku: product.sku,
        quantity: product.quantity || 1
      }));

      const response: { pricing: (Product & { sell?: number | null })[] } =
        await mutation.mutateAsync({
          type,
          body: {
            accountNumber,
            products,
            ...(pricingType && { type: pricingType }),
            ...(deliveryDate && { deliveryDate })
          }
        });

      const rows = response?.pricing || [];
      // The response normally comes back one row per requested product, in
      // order. When it doesn't (a SKU dropped out), fall back to matching by
      // SKU so `id` never lands on the wrong product.
      const aligned = rows.length === productList?.length;

      const priced: ProductPrice[] = rows.map((productPricing, idx) => {
        const source = aligned
          ? productList[idx]
          : productList?.find(p => p.sku === productPricing?.sku);
        // `sell: null` means pricing ran and produced nothing. `sell: 0` is a
        // real price for a free item — never flag it.
        const failed = productPricing?.sell == null;

        return {
          ...productPricing,
          id: source?.id || null,
          price: failed ? null : Number(productPricing.sell),
          pricingFailed: failed,
          pricingFailureReason: failed ? NO_PRICE_RETURNED : null
        };
      });

      // Requested SKUs the response left out entirely — also a failure, and one
      // the callers can only see if we surface a row for it.
      const returnedSkus = new Set(rows.map(row => row?.sku).filter(Boolean));
      const missing: ProductPrice[] = (productList || [])
        .filter(product => product.sku && !returnedSkus.has(product.sku))
        .map(
          (product): ProductPrice => ({
            sku: product.sku,
            id: product.id || null,
            price: null,
            pricingFailed: true,
            pricingFailureReason: SKU_NOT_PRICED
          })
        );

      return [...priced, ...missing];
    } catch (error) {
      const axiosError = error as AxiosError<any>;

      enqueueSnackbar('Unable to retrieve product prices. Please try again.', {
        variant: 'error',
        // An AxiosError stringifies to a wall of config/request noise, so
        // "Details" showed nothing worth reading. The server's own payload (or
        // failing that, the axios message) is what says why.
        details: axiosError?.response?.data || axiosError?.message,
        autoHideDuration: 8000
      });
      // NULL means "the call failed", distinct from an empty array ("nothing
      // came back priced"). Callers must handle it — see the guard in the order
      // create context.
      return null;
    }
  };

  return { getPrices, isLoading: mutation.isLoading };
};
